// -----------------------------------------------
// Imports
// -----------------------------------------------
import Mustache from '@core/utils/mustache.js';
if (typeof window !== 'undefined') {
  window.Mustache = Mustache;
}
import MOJOUtils from '@core/utils/MOJOUtils.js';
import EventDelegate from '@core/mixins/EventDelegate.js';

// -----------------------------------------------
// View
// -----------------------------------------------
export class View {
  // ---------------------------------------------
  // Construction & defaults
  // ---------------------------------------------
  constructor(opts = {}) {
    this.tagName     = opts.tagName ?? "div";
    this.className   = opts.className ?? "mojo-view";
    this.style       = opts.style ?? null;                  // inline css text
    this.id          = opts.id ?? View._genId();            // element id
    this.containerId = opts.containerId ?? null;            // container to render into
    this.container   = opts.container ?? null;              // container element

    // If container is a string, treat it as containerId and set container to null
    if (typeof this.container === 'string') {
      this.containerId = this.container;
      this.container = null;
    }
    this.parent      = opts.parent ?? null;                 // parent view
    this.children    = opts.children ?? {};                 // dict of id -> child view
    this.template    = opts.template || opts.templateUrl || "";                 // string or function(model) -> string
    this.data        = opts.data ?? {};                    // data for Mustache or basic templating
    this.isRendering     = false;
    this.lastRenderTime  = 0;
    this.mounted         = false;
    this.debug           = opts.debug ?? false;
    this.app           = opts.app ?? null;
    this.cacheTemplate = opts.cacheTemplate ?? true;
    this.enableTooltips = opts.enableTooltips ?? false;  // Auto-initialize Bootstrap tooltips after render

    // keep original options
    this.options = { ...opts };

    // internal DOM element
    this.element = this._ensureElement();

    // in constructor
    this.events = new EventDelegate(this);

    if (opts.model) this.setModel(opts.model);

  }

  // ---------------------------------------------
  // Lifecycle hooks (overridable)
  // ---------------------------------------------
  async onInit() {

  }
  async onInitView() {
      if (this.initialized) return;
      this.initialized = true;
      await this.onInit();
  }
  async onBeforeRender() {}
  async onAfterRender() {}
  async onBeforeMount() {}
  async onAfterMount() {}
  async onBeforeUnmount() {}
  async onAfterUnmount() {}
  async onBeforeDestroy() {}
  async onAfterDestroy() {}

  // ---------------------------------------------
  // Public API
  // ---------------------------------------------
  setModel(model = {}) {
      let isDiff = model !== this.model;
      if (!isDiff) return this;
      if (this.model && this.model.off) {
          this.model.off("change", this._onModelChange, this);
      }
      this.model = model;
      if (this.model && this.model.on) {
          this.model.on("change", this._onModelChange, this);
      }

      // Set model on all children
      for (const id in this.children) {
          const child = this.children[id];
          if (child && typeof child.setModel === 'function') {
              child.setModel(model);
          }
      }

      if (isDiff ) {
          this._onModelChange();
      }
      return this;
  }

  _onModelChange(model, options) {
    // `skipRender` set()/save() option suppresses the automatic rerender —
    // used by inline-autosave forms that already update their DOM in place.
    if (options && options.skipRender) return;
    if (this.isMounted()) {
        this.render();
    }
  }

  setTemplate(tpl)     { this.template = tpl ?? ""; return this; }

  addChild(childView, options) {
    try {
      if (!childView || typeof childView !== "object") return this;

      // Allow overriding containerId and id via options
      if (options) {
        if (options.containerId || options.container) {
          childView.containerId = options.containerId || options.container;
        }
        if (options.id) {
          childView.id = options.id;
        }
        // lazyMount: defer the child's render until its container scrolls
        // into view. Useful for long dashboards where below-the-fold
        // panels would otherwise fetch on first paint.
        if (options.lazyMount === true) {
          childView._lazyMount = true;
        }
      }

      childView.parent = this;
      if (this.getApp()) childView.app = this.app;
      this.children[childView.id] = childView;
    } catch (e) { View._warn("addChild error", e); }
    return childView;
  }

  removeChild(idOrView) {
    try {
      const id = typeof idOrView === "string" ? idOrView : (idOrView && idOrView.id);
      if (!id) return this;
      const child = this.children[id];
      if (child) {
        // allow for async or sync destroy
        Promise.resolve(child.destroy()).catch(err => View._warn("removeChild destroy error", err));
        delete this.children[id];
      }
    } catch (e) { View._warn("removeChild error", e); }
    return this;
  }

  getChild(id) {
    return this.children[id];
  }

  async updateData(newData, rerender = false) {
    Object.assign(this.data, newData);

    if (rerender && this.isMounted()) {
      await this.render();
    }

    return this;
  }

  toggleClass(className, force) {
    if (force === undefined) {
      force = !this.element.classList.contains(className);
    }
    this.element.classList.toggle(className, force);
    return this;
  }

  addClass(className) {
    this.element.classList.add(className);
    return this;
  }

  setClass(className) {
    this.element.className = className;
    return this;
  }

  removeClass(className) {
    this.element.classList.remove(className);
    return this;
  }

  canRender() {
      if (this.isRendering) return false;
      // Optional render throttling
      const now = Date.now();
      if (this.options.renderCooldown > 0 && now - this.lastRenderTime < this.options.renderCooldown) {
        View._warn(`View ${this.id}: Render called too quickly, cooldown active`);
        return false;
      }
      if (this.options.noAppend) {
        if (this.parent) {
          if (!this.parent.contains(this.containerId || this.container)) {
              return false;
          } else if (this.containerId && !document.getElementById(this.containerId)) {
              return false;
          } else if (this.container && !document.contains(this.container)) {
              return false;
          }
        }
      }
      return true;
  }

  // ---------------------------------------------
  // Render flow
  // ---------------------------------------------
  async render(allowMount = true, container = null) {
    const now = Date.now();

    if (!this.canRender()) {
        return this;
    }

    this.isRendering = true;
    this.lastRenderTime = now;

    try {
      if (!this.initialized) await this.onInitView();
      this.unbindEvents();

      await this.onBeforeRender();
      if (this.getViewData) {
          this.data = await this.getViewData();
      }
      // 1) render own HTML (FIX #5: await the async template render)
      const html = await this.renderTemplate();
      this.element.innerHTML = html;

      if (allowMount && !this.isMounted()) {
          await this.mount(container)
      }

      // 3) render children
      await this._renderChildren();
      await this.onAfterRender();
      this.bindEvents();

    } catch (e) {
      View._warn(`Render error in ${this.id}`, e);
    } finally {
      // FIX #4: always reset isRendering
      this.isRendering = false;
    }

    return this;
  }

  async _renderChildren() {
    const lazyPending = [];
    for (const id in this.children) {
      const child = this.children[id];
      if (!child) continue;
      child.parent = this;
      if (child._lazyMount && !child._lazyTriggered) {
        // Skip render — observer will mount it when its container enters
        // the viewport. Track it for observer setup below.
        lazyPending.push(child);
        continue;
      }
      await Promise.resolve(child.render()).catch(err => View._warn(`Child render error (${id})`, err));
    }
    if (lazyPending.length) this._setupLazyMountObserver(lazyPending);
  }

  /**
   * Start an IntersectionObserver per pending lazy child. When the
   * container scrolls into view, mark the child as triggered and render.
   * @private
   */
  _setupLazyMountObserver(pendingChildren) {
    if (typeof IntersectionObserver === 'undefined') {
      // SSR / unsupported — fall back to immediate render so behavior
      // degrades gracefully.
      pendingChildren.forEach(c => {
        c._lazyTriggered = true;
        Promise.resolve(c.render()).catch(err => View._warn(`Lazy child render error (${c.id})`, err));
      });
      return;
    }

    if (!this._lazyObserver) {
      this._lazyObserver = new IntersectionObserver((entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const child = entry.target.__mojoLazyChild;
          observer.unobserve(entry.target);
          if (!child || child._lazyTriggered) continue;
          child._lazyTriggered = true;
          Promise.resolve(child.render()).catch(err => View._warn(`Lazy child render error (${child.id})`, err));
        }
      }, {
        rootMargin: '120px 0px',  // start mounting just before scroll-in
        threshold: 0.01
      });
    }

    for (const child of pendingChildren) {
      const container = this.getChildElement(child.containerId);
      if (!container) continue;
      container.__mojoLazyChild = child;
      // IntersectionObserver doesn't fire on 0-height elements. Give the
      // placeholder a minimal height so the observer can measure it; this
      // is overridden once the real child mounts and fills the container.
      if (!container.style.minHeight) {
        container.classList.add('mojo-lazy-placeholder');
        container.style.minHeight = '1px';
      }
      this._lazyObserver.observe(container);

      // Belt-and-suspenders: IntersectionObserver's initial callback can
      // be flaky inside scrolled sub-containers (the dashboard renders
      // inside a separately-scrolled .portal-content panel). On the next
      // tick, manually check whether the container is in the viewport and
      // trigger if so. The normal observer still handles future scrolls.
      requestAnimationFrame(() => {
        if (child._lazyTriggered) return;
        const rect = container.getBoundingClientRect();
        const vh = (typeof window !== 'undefined') ? window.innerHeight : 0;
        const vw = (typeof window !== 'undefined') ? window.innerWidth  : 0;
        const visible = rect.top < (vh + 120) && rect.bottom > -120 && rect.left < vw && rect.right > 0;
        if (visible) {
          child._lazyTriggered = true;
          this._lazyObserver?.unobserve(container);
          Promise.resolve(child.render()).catch(err => View._warn(`Lazy child render error (${child.id})`, err));
        }
      });
    }
  }

  async _unmountChildren() {
    for (const id in this.children) {
      const child = this.children[id];
      if (!child) continue;
      child.unbindEvents()
    }
  }


  isMounted() {
    return this.element?.isConnected;
  }

  getChildElementById(id, root = null) {
    const cleanId = id.startsWith('#') ? id.substring(1) : id;
    if (root) {
        return root.querySelector(`#${cleanId}`);
    }
    return this.element.querySelector(`#${cleanId}`);
  }

  getChildElement(id) {
    if (id.startsWith("#")) {
        return this.getChildElementById(id);
    }
    let el = this.element?.querySelector(`[data-container="${id}"]`);
    if (!el) {
        return this.getChildElementById(id);
    }
    return el;
  }

  getContainer() {
      if (this.replaceById) {
          // this means we want to place the element in the dom that matches our id
          if (this.parent) {
              return this.parent.getChildElementById(this.id);
          }
          // return this.getChildElementById(this.id, document.body);
          return null;
      }
      if (!this.containerId) return null;
      if (this.parent) {
          return this.parent.getChildElement(this.containerId);
      }
      return this.getChildElementById(this.containerId, document.body);
  }

  async mount(container =  null) {
      await this.onBeforeMount();
      if (!container) {
          container = this.getContainer();
      }

      if (this.containerId && !container) {
          // throw new Error(`Container not found for ${this.containerId}`);
          console.error(`Container not found for ${this.containerId}`);
          return;
      }

      if (container && this.replaceById) {
          container.replaceWith(this.element);
      } else if (container) {
          // if we have a container just replace its children with our element
          container.replaceChildren(this.element);
      } else if (!this.containerId && this.parent) {
          // append to parent
          this.parent.element.appendChild(this.element);
      } else if (!this.containerId && !this.parent && this.options.allowAppendToBody) {
          // append to body
          console.log("APPENDING TO BODY!!!!");
          document.body.appendChild(this.element);
      } else {
          // there is a containerId but no container
          console.error(`Container not found for ${this.containerId}`);
      }

      await this.onAfterMount();
      this.mounted = true;
  }

  async unmount() {
      if (!this.element || !this.element.parentNode) return;
      await this.onBeforeUnmount();
      // unbind all children
      await this._unmountChildren();
      if (this.element.parentNode) this.element.parentNode.removeChild(this.element);
      this.events.unbind();
      await this.onAfterUnmount();
      this.mounted = false;
  }

  // FIX #1: make destroy async (it already awaited hooks)
  async destroy() {
    try {
        this.events.unbind();
        if (this._lazyObserver) {
            this._lazyObserver.disconnect();
            this._lazyObserver = null;
        }
      // destroy children first (support async or sync destroy)
      for (const id in this.children) {
        const ch = this.children[id];
        if (ch) {
          await Promise.resolve(ch.destroy()).catch(err => View._warn(`Child destroy error (${id})`, err));
        }
      }
      this.mounted = false;
      if (this.element && this.element.parentNode) {
        await this.onBeforeDestroy();
        if (this.element.parentNode) this.element.parentNode.removeChild(this.element);
        await this.onAfterDestroy();
      }
    } catch (e) {
      View._warn(`Destroy error in ${this.id}`, e);
    }
  }

  // ---------------------------------------------
  // DOM helpers
  // ---------------------------------------------
  _ensureElement() {
    try {
      if (this.element && this.element.tagName?.toLowerCase() === this.tagName) {
        this._syncAttrs();
        return this.element;
      }
      const el = document.createElement(this.tagName);
      this.element = el;
      this.el = el;
      this._syncAttrs();
      return el;
    } catch (e) {
      View._warn("ensureElement error", e);
      // last resort: create a DIV to avoid throwing
      const el = document.createElement("div");
      // FIX #2: use View._genId() (not SimpleView)
      el.id = this.id || View._genId();
      return el;
    }
  }

  _syncAttrs() {
    try {
      if (!this.element) return;
      if (this.id) this.element.id = this.id;
      this.element.className = this.className || "";
      if (this.style == null) {
        this.element.removeAttribute("style");
      } else {
        this.element.style.cssText = String(this.style);
      }
    } catch (e) { View._warn("_syncAttrs error", e); }
  }

  bindEvents() {
      this.events.bind(this.element);

      // Initialize tooltips if enabled
      if (this.enableTooltips) {
          this.initializeTooltips();
      }
  }

  unbindEvents() {
      this.events.unbind();

      // Cleanup tooltips if enabled
      if (this.enableTooltips) {
          this.disposeTooltips();
      }
  }

  // ---------------------------------------------
  // Template helpers
  // ---------------------------------------------
  async renderTemplate() {
    const templateContent = await this.getTemplate();
    if (!templateContent) return '';
    const partials = this.getPartials();
    return Mustache.render(templateContent, this, partials);
  }

  renderTemplateString(template, context, partials) {
    // Mustache.render now auto-wraps context to enable pipe formatters
    return Mustache.render(template, context, partials);
  }

  getPartials() { return {}; }

  async getTemplate() {
    if (this._templateCache && this.cacheTemplate) {
      return this._templateCache;
    }
    const template = this.template || this.templateUrl;
    if (!template) {
      throw new Error('Template not found');
    }

    let templateContent = '';

    if (typeof template === 'string') {
      if (template.includes('<') || template.includes('{')) {
          templateContent = template;
      } else {
        try {
          let templatePath = template;
          if (!this.app) this.app = this.getApp();
          if (this.app && this.app.basePath) {
            if (!templatePath.startsWith('/') &&
                !templatePath.startsWith('http://') &&
                !templatePath.startsWith('https://')) {
              const base = this.app.basePath.endsWith('/')
                  ? this.app.basePath.slice(0, -1)
                  : this.app.basePath;
              templatePath = `${base}/${templatePath}`;
            }
          }

          const response = await fetch(templatePath);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          templateContent = await response.text();
        } catch (error) {
          View._warn(`Failed to load template from ${template}: ${error}`);
          // NOTE: showError may be provided by host app
          this.showError?.(`Failed to load template from ${template}: ${error.message}`);
        }
      }
    } else if (typeof template === 'function') {
      templateContent = await this.template(this.data, this.state);
    }

    // Cache only string templates. Function templates are meant to be
    // re-evaluated against current view state on every render — caching
    // their first result would freeze them and break reactivity (e.g.
    // a value computed from `this.value` would never update).
    if (this.cacheTemplate && templateContent && typeof template !== 'function') {
      this._templateCache = templateContent;
    }

    return templateContent;
  }

  getContextValue(path) {
    const value = MOJOUtils.getContextData(this, path);

    if (path && path.startsWith('data.') && value && typeof value === 'object') {
      return MOJOUtils.wrapData(value, this);
    }

    if (path && path.startsWith('model.') &&
        path !== 'model' &&
        value && typeof value === 'object' &&
        typeof value.getContextValue !== 'function') {
      return MOJOUtils.wrapData(value, null);
    }

    return value;
  }

  // 9) Navigation Helpers ------------------------------------------------------
  async handlePageNavigation(element) {
    const pageName = element.getAttribute('data-page');
    const paramsAttr = element.getAttribute('data-params');
    let params = {};
    if (paramsAttr) {
      try { params = JSON.parse(paramsAttr); }
      catch (error) { console.warn('Invalid JSON in data-params:', paramsAttr); }
    }

    const app = this.getApp();
    if (app) { app.showPage(pageName, params); return; }

    const router = this.findRouter();
    if (router && typeof router.navigateToPage === 'function') {
      await router.navigateToPage(pageName, params);
    } else {
      console.error(`No router found for page navigation to '${pageName}'`);
    }
  }

  async handleHrefNavigation(element) {
    const href = element.getAttribute('href');
    if (this.isExternalLink(href) || element.hasAttribute('data-external')) return;

    const router = this.findRouter();
    if (router) {
      if (router.options && router.options.mode === 'param' && href.startsWith('?')) {
        const fullPath = '/' + href;
        await router.navigate(fullPath);
        return;
      }
      if (router.options && router.options.mode === 'hash' && href.startsWith('#')) {
        await router.navigate(href);
        return;
      }
      const routePath = this.hrefToRoutePath(href);
      await router.navigate(routePath);
    } else {
      console.warn('No router found for navigation, using default behavior');
      window.location.href = href;
    }
  }

  isExternalLink(href) {
    if (!href) return true;
    if (href.startsWith("/") && this.getApp()) {
        if (href.startsWith(this.findRouter().basePath)) return false;
        return true;
    }
    return href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//');
  }

  hrefToRoutePath(href) {
    if (href.startsWith('/')) {
      const router = this.findRouter();
      if (router && router.options && router.options.base) {
        const base = router.options.base;
        if (href.startsWith(base)) return href.substring(base.length) || '/';
      }
      return href;
    }
    return href.startsWith('./') ? href.substring(2) : href;
  }

  findRouter() {
    this.getApp();
    return this.app?.router || null;
  }

  getApp() {
    if (this.app) return this.app;
    const apps = [
      window.__app__,
      window.MOJO?.app,
      window.APP,
      window.app,
      window.WebApp,
      window.matchUUID ? window[window.matchUUID]() : window[window.matchUUID]
    ];
    this.app = apps.find(app => app && typeof app.showPage === 'function') || null;
    return this.app;
  }

  /**
   * Permission gate for `permissions` keys on toolbar buttons, context-menu
   * items, etc. Fail-closed: when permissions are given but no active user
   * can be resolved (or the user lacks them), the gated element is hidden.
   * @param {string|string[]} permissions - permission name(s); any-of for arrays
   * @returns {boolean}
   */
  checkPermissions(permissions) {
    if (!permissions) return true;
    const user = this.getApp()?.activeUser;
    return !!(user && typeof user.hasPermission === 'function' && user.hasPermission(permissions));
  }

  handleActionError(action, err, evt, el) {
      this.showError(`Action '${action}' failed: ${err}`, evt, el);
  }

  // ---------------------------------------------
  // Utilities
  // ---------------------------------------------
  /**
   * Escape HTML characters
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  escapeHtml(str) {
    if (typeof str !== 'string') return str;

    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  contains(el) {
      if (typeof el === 'string') {
        if (!this.element) return false; // no parent element yet
        el = document.getElementById(el);
      }
      if (!el) return false; // no element with that id
      return this.element.contains(el);
  }

  /**
   * Initialize Bootstrap tooltips in this view's element
   * Called automatically in bindEvents() if enableTooltips is true
   */
  initializeTooltips() {
      if (!this.element || !window.bootstrap?.Tooltip) return;
      this.disposeTooltips();
      const tooltipTriggerList = this.element.querySelectorAll('[data-bs-toggle="tooltip"]');
      [...tooltipTriggerList].map(tooltipTriggerEl => {
          // Extract custom options from data attributes
          const theme = tooltipTriggerEl.getAttribute('data-tooltip-theme');
          const size = tooltipTriggerEl.getAttribute('data-tooltip-size');

          // Build custom class list
          let customClass = '';
          if (theme) customClass += `tooltip-${theme} `;
          if (size) customClass += `tooltip-${size}`;

          // Build options object - only include customClass if it has a value
          const options = {};
          const trimmedClass = customClass.trim();
          if (trimmedClass) {
              options.customClass = trimmedClass;
          }

          // Initialize tooltip with custom options
          return new window.bootstrap.Tooltip(tooltipTriggerEl, options);
      });
  }

  /**
   * Dispose all Bootstrap tooltips in this view's element
   * Called automatically in unbindEvents() if enableTooltips is true
   */
  disposeTooltips() {
      if (!this.element || !window.bootstrap?.Tooltip) return;

      const tooltipElements = this.element.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipElements.forEach(element => {
          const tooltip = window.bootstrap.Tooltip.getInstance(element);
          if (tooltip) {
              tooltip.dispose();
          }
      });
  }

  /**
   * Show error message
   * @param {string} message - Error message
   */
  async showError(message) {
    console.error(`View ${this.id} error:`, message);
    const app = this.getApp ? this.getApp() : (this.app || null);
    if (app && typeof app.showError === 'function') {
      await app.showError(message);
      return;
    }
    // Fallback to browser alert if no app or helper available
    alert(`Error: ${message}`);
  }

  /**
   * Show success message
   * @param {string} message - Success message
   */
  async showSuccess(message) {
    if (this.debug) {
      console.log(`View ${this.id} success:`, message);
    }
    const app = this.getApp ? this.getApp() : (this.app || null);
    if (app && typeof app.showSuccess === 'function') {
      await app.showSuccess(message);
      return;
    }
    // Fallback to browser alert if no app or helper available
    alert(`Success: ${message}`);
  }

  /**
   * Show info message
   * @param {string} message - Info message
   */
  async showInfo(message) {
    console.info(`View ${this.id} info:`, message);
    const app = this.getApp ? this.getApp() : (this.app || null);
    if (app && typeof app.showInfo === 'function') {
      await app.showInfo(message);
      return;
    }
    // Fallback to browser alert if no app or helper available
    alert(`Info: ${message}`);
  }

  /**
   * Show warning message
   * @param {string} message - Warning message
   */
  async showWarning(message) {
    console.warn(`View ${this.id} warning:`, message);
    const app = this.getApp ? this.getApp() : (this.app || null);
    if (app && typeof app.showWarning === 'function') {
      await app.showWarning(message);
      return;
    }
    // Fallback to browser alert if no app or helper available
    alert(`Warning: ${message}`);
  }

  // Default action: copy value from data-clipboard to clipboard
  async onActionCopyToClipboard(event, element) {
    try {
      const carrier = element?.closest('[data-clipboard]') || element;
      const text = carrier?.getAttribute('data-clipboard') || '';
      // Consider handled even if empty to avoid bubbling
      if (!text) return true;

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      // Visual feedback: temporarily swap icon to a checkmark
      const icon = element.querySelector('i');
      const originalClass = icon && icon.className;
      if (icon) {
        icon.className = 'bi bi-check';
        setTimeout(() => { icon.className = originalClass; }, 1000);
      }
      return true;
    } catch (err) {
      console.warn('Copy to clipboard failed:', err);
      return true;
    }
  }

  static _genId() { return `view_${Math.random().toString(36).substr(2, 9)}`; }

  static _warn(msg, err) {
    try {
      if (err) console.warn(`[View] ${msg}:`, err);
      else console.warn(`[View] ${msg}`);
    } catch { /* never throw on logging */ }
  }
}

import EventEmitter from '@core/mixins/EventEmitter.js';
Object.assign(View.prototype, EventEmitter);

export default View;
